import { useCallback, useEffect, useState } from "react";
import { Copy, Share2, Gavel } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Badge } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const badgeFor = (s) =>
  s === "accepted" ? "bg-green-50 text-green-900"
  : s === "declined" ? "bg-red-50 text-red-900"
  : s === "superseded" ? "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
  : "bg-amber-50 text-amber-900";

/** Panel of registered arbitrators; an advocate may request any of them to take a reference. */
export const ArbitratorPanel = ({ cases }) => {
  const [rows, setRows] = useState([]);
  const [caseId, setCaseId] = useState("");
  const [made, setMade] = useState(null);
  const [busy, setBusy] = useState("");
  const [tag, setTag] = useState({ email: "", name: "", phone: "" });

  const load = useCallback(() => {
    api.get("/arbitrators/panel", { params: caseId ? { case_id: caseId } : {} })
      .then(({ data }) => setRows(data)).catch(() => {});
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  const invite = async (arb) => {
    if (!caseId) return toast.error("Choose the reference first");
    setBusy(arb.id);
    try {
      const { data } = await api.post(`/cases/${caseId}/arbitrator-invites`, { arbitrator_id: arb.id });
      setMade({ ...data, name: arb.name });
      toast.success(`${arb.name} added to the tribunal — email sent; share the link on WhatsApp too`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(made.url);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy — select and copy the link manually");
    }
  };

  return (
    <div className="border border-border bg-card mt-6" data-testid="arbitrator-panel">
      <div className="px-6 py-4 border-b border-border flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <p className="label-caps">Panel of arbitrators</p>
          <p className="text-xs text-muted-foreground mt-1">
            Add an arbitrator to a reference. They go on record at once and receive an email with an
            acceptance link; if one declines, the seat falls vacant and you may request another.
          </p>
        </div>
        <div className="space-y-2 w-full sm:w-72">
          <Label className="label-caps">Reference</Label>
          <Select value={caseId} onValueChange={setCaseId}>
            <SelectTrigger className="rounded-none h-10" data-testid="panel-case-select">
              <SelectValue placeholder="Select a reference" />
            </SelectTrigger>
            <SelectContent>
              {(cases || []).map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.case_number || c.ref_id} · {c.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <form className="mx-6 my-5 border border-dashed border-border p-4 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end" data-testid="tag-arbitrator-form"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!caseId) return toast.error("Choose the reference first");
          setBusy("email");
          try {
            const { data } = await api.post(`/cases/${caseId}/arbitrator-invites`, { email: tag.email, name: tag.name || undefined, phone: tag.phone || undefined });
            setMade({ ...data, name: data.arbitrator_name });
            toast.success(`${data.arbitrator_name} tagged — acceptance link issued`);
            setTag({ email: "", name: "", phone: "" });
            load();
          } catch (err) { toast.error(errMsg(err)); } finally { setBusy(""); }
        }}>
        <div className="sm:col-span-3">
          <p className="label-caps">Or tag an arbitrator by e-mail</p>
          <p className="text-xs text-muted-foreground mt-1">Not on the approved list yet? Tag them by e-mail; they receive a set-password link and go to the registry for approval.</p>
        </div>
        <div className="space-y-2"><Label className="label-caps">E-mail *</Label>
          <input type="email" required value={tag.email} onChange={(e) => setTag((t) => ({ ...t, email: e.target.value }))} data-testid="tag-arbitrator-email"
            className="w-full h-10 px-3 border border-border bg-background text-sm rounded-none" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2"><Label className="label-caps">Name</Label>
            <input value={tag.name} onChange={(e) => setTag((t) => ({ ...t, name: e.target.value }))} data-testid="tag-arbitrator-name"
              className="w-full h-10 px-3 border border-border bg-background text-sm rounded-none" /></div>
          <div className="space-y-2"><Label className="label-caps">WhatsApp</Label>
            <input value={tag.phone} onChange={(e) => setTag((t) => ({ ...t, phone: e.target.value }))} data-testid="tag-arbitrator-phone" placeholder="+91…"
              className="w-full h-10 px-3 border border-border bg-background text-sm rounded-none" /></div>
        </div>
        <Button type="submit" disabled={busy === "email"} className="rounded-none bg-blue-900 hover:bg-blue-800 text-white uppercase tracking-widest text-xs h-10" data-testid="tag-arbitrator-submit">Tag by e-mail</Button>
      </form>

      {made && (
        <div className="mx-6 my-5 border border-blue-900/40 bg-blue-50 dark:bg-blue-950/30 p-5" data-testid="arb-invite-created">
          <p className="label-caps">Acceptance link for {made.name}</p>
          <p className="font-mono text-xs break-all mt-2" data-testid="arb-invite-link">{made.url}</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Button onClick={copy} data-testid="arb-invite-copy"
              className="rounded-none bg-slate-900 hover:bg-slate-800 text-white uppercase tracking-widest text-xs h-10">
              <Copy className="h-3.5 w-3.5 mr-2" /> Copy link
            </Button>
            <a href={made.whatsapp_url} target="_blank" rel="noreferrer" data-testid="arb-invite-whatsapp"
              className="inline-flex items-center h-10 px-4 bg-green-700 hover:bg-green-600 text-white uppercase tracking-widest text-xs transition-colors duration-200">
              <Share2 className="h-3.5 w-3.5 mr-2" /> Send on WhatsApp
            </a>
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <p className="px-6 py-10 text-sm text-muted-foreground">No arbitrators are registered yet.</p>
      ) : rows.map((a) => (
        <div key={a.id} className="px-6 py-4 border-t border-border flex flex-wrap items-center justify-between gap-4"
          data-testid={`panel-arbitrator-${a.id}`}>
          <div className="min-w-0">
            <p className="text-sm font-medium flex items-center gap-2">
              <Gavel className="h-3.5 w-3.5 text-blue-900 dark:text-blue-400" /> {a.name}
            </p>
            <p className="text-xs text-muted-foreground mt-1 font-mono truncate">
              {[a.city, a.phone, a.email].filter(Boolean).join(" · ")}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {a.invite_status && (
              <Badge className={badgeFor(a.invite_status)} testId={`panel-status-${a.id}`}>{a.invite_status}</Badge>
            )}
            {a.invite_status !== "accepted" && a.invite_status !== "pending" && (
              <Button onClick={() => invite(a)} disabled={!!busy} data-testid={`panel-invite-${a.id}`}
                className="rounded-none bg-blue-900 hover:bg-blue-800 text-white uppercase tracking-widest text-xs h-10 px-4">
                {busy === a.id ? "Adding…" : a.invite_status === "declined" ? "Ask again" : "Add to case"}
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

/** Pending requests awaiting the arbitrator's acceptance. */
export const ArbitratorRequests = ({ onChange }) => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/arbitrator-invites").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const respond = async (id, action) => {
    setBusy(id + action);
    try {
      await api.post(`/arbitrator-invites/${id}/respond`, { action });
      toast.success(action === "accept" ? "Reference accepted — it is now in your active cases" : "Reference declined");
      load();
      onChange?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  if (rows.length === 0) return null;

  return (
    <div className="border-2 border-blue-900 bg-blue-50 dark:bg-blue-950/30 mt-6" data-testid="arbitrator-requests">
      <div className="px-6 py-4 border-b border-blue-900/30">
        <p className="label-caps">Pending case invitations</p>
      </div>
      {rows.map((r) => (
        <div key={r.id} className="px-6 py-5 border-b border-blue-900/20 last:border-0" data-testid={`arb-request-${r.id}`}>
          <p className="font-display text-xl">{r.case_title}</p>
          <p className="font-mono text-xs text-muted-foreground mt-1">
            {r.case_number || r.case_ref} · {r.claimant} vs {r.respondent}
            {r.seat ? ` · seat ${r.seat}` : ""}
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            Requested by {r.invited_by_name}
            {r.claim_amount ? ` · claim ${r.currency} ${Number(r.claim_amount).toLocaleString("en-IN")}` : ""}
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Button onClick={() => respond(r.id, "accept")} disabled={!!busy} data-testid={`arb-accept-${r.id}`}
              className="rounded-none bg-blue-900 hover:bg-blue-800 text-white uppercase tracking-widest text-xs h-10 px-5">
              {busy === r.id + "accept" ? "Accepting…" : "Accept reference"}
            </Button>
            <Button onClick={() => respond(r.id, "decline")} disabled={!!busy} data-testid={`arb-decline-${r.id}`}
              className="rounded-none bg-red-800 hover:bg-red-700 text-white uppercase tracking-widest text-xs h-10 px-5">
              {busy === r.id + "decline" ? "Declining…" : "Decline"}
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};
